import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  MouseEvent,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router';

import { popup } from '@/actions/ui';
import { SignupType, formatAuthPath } from '@/auth';
import AuthLayout from '@/auth/components/AuthLayout';
import { PasswordTextField, PasswordTextFieldProps } from '@/auth/components/PasswordTextField';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Link from '@/components/Link';
import { GroupDomain } from '@/group';
import { useAppDispatch } from '@/store';
import { borderColor, darkGray } from '@/theme/colors';
import { getJoinTitle } from '@/utils/reducer';

const useStyles = makeStyles({
  form: {
    textAlign: 'center',
    margin: '0 auto',
  },
  title: {
    marginTop: 0,
  },
  button: {
    fontWeight: 600,
    marginTop: 30,
    padding: '8px 22px',
    fontSize: 15,
  },
  signup: {
    color: darkGray,
  },
  // Without Google and Microsoft the card stays as it was before them.
  plainForm: {
    maxWidth: 400,
  },
  plainButton: {
    marginBottom: 30,
    maxWidth: 200,
  },
  linkedin: {
    cursor: 'pointer',
  },
  loginDivider: {
    backgroundColor: borderColor,
    margin: '40px 0 30px',
  },
});

/** The links under Log in when no social button is offered, as before ENG-6165. */
function PlainLoginLinks({ forgotTo }: { forgotTo: string }) {
  const s = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Link renders a bare <button> inside the login form: keep the click from submitting it.
  const handleLinkedInClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      e.preventDefault();
      dispatch(
        popup({
          title: 'LinkedIn authentication is no longer available',
          contents: 'Please use your email and password to login.',
          buttonAlignment: 'space-between',
          buttons: [
            {
              label: 'Create new password',
              primary: true,
              callback: () => navigate('/password_reset'),
            },
            { flat: true, label: 'Cancel' },
          ],
        })
      );
    },
    [dispatch, navigate]
  );

  return (
    <>
      <div>
        <Link id="loginForgotPassword" to={forgotTo}>
          Forgot your password?
        </Link>

        <br />
        <br />

        <Link id="loginLinkedInLogin" className={s.linkedin} onClick={handleLinkedInClick}>
          Missing LinkedIn login?
        </Link>
      </div>

      <Divider className={s.loginDivider} />
    </>
  );
}

interface CredentialsProps {
  nextUrl?: string;
  signupType?: SignupType;
  invite?: string;
  domain?: GroupDomain;
  signupLink?: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onEmailChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onPasswordChange: PasswordTextFieldProps['onChange'];
  email: string;
  password: string;
  errors: string[];
  notice?: string;
  buttonDisable: boolean;
  alertId?: string;
  /**
   * Shown below the Log In button: the social sign-in buttons (ENG-6165, ENG-6169).
   * Without it the card keeps its earlier layout, LinkedIn notice included.
   */
  afterSubmit?: ReactNode;
  emailRef?: Ref<HTMLInputElement>;
}

const Credentials: FC<CredentialsProps> = ({
  nextUrl,
  signupType,
  invite,
  domain,
  signupLink,
  onSubmit,
  onEmailChange,
  onPasswordChange,
  email,
  password,
  errors,
  notice,
  buttonDisable,
  alertId,
  afterSubmit,
  emailRef,
}) => {
  const s = useStyles();
  const social = Boolean(afterSubmit);
  const forgotTo = `/password_reset?next=${encodeURIComponent(nextUrl ?? '')}`;

  const errorBlock =
    errors.length > 0 ? (
      <Alert type="error" center id={alertId}>
        {errors[0]}
      </Alert>
    ) : undefined;

  // Why the user was sent here (ENG-5760); an error takes its place.
  const noticeBlock =
    notice && errors.length === 0 ? (
      <Alert preset="notification" center>
        {notice}
      </Alert>
    ) : undefined;

  const form = (
    <form className={clsx(s.form, social ? 'max-w-xl' : s.plainForm)} onSubmit={onSubmit}>
      {!domain && <h2 className={s.title}>Log In</h2>}

      <TextField
        id="loginEmail"
        variant="outlined"
        fullWidth
        type="email"
        label="Email address"
        inputProps={{
          autoComplete: 'username',
        }}
        inputRef={emailRef}
        value={email}
        onChange={onEmailChange}
      />
      <PasswordTextField
        id="loginPassword"
        endAdornmentId="loginEye"
        variant="outlined"
        fullWidth
        label="Password"
        value={password}
        onChange={onPasswordChange}
      />

      <Button
        id="loginButton"
        disabled={buttonDisable}
        type="submit"
        size="medium"
        classes={{ root: clsx(s.button, !social && s.plainButton) }}
        className={social ? 'mb-24' : undefined}
        fullWidth
      >
        Log in
      </Button>

      {social ? (
        <>
          <div className="mb-24">{afterSubmit}</div>
          <div>
            <Link id="loginForgotPassword" to={forgotTo}>
              Forgot your password?
            </Link>
          </div>
        </>
      ) : (
        <PlainLoginLinks forgotTo={forgotTo} />
      )}

      <div className={clsx(s.signup, social && 'mt-24')}>
        Do not have an account?
        <Link
          id="loginSignUp"
          to={formatAuthPath({
            to: signupLink || '/signup',
            domain,
            signupType,
            invite,
            next: nextUrl,
          })}
        >
          {' '}
          Sign up
        </Link>
      </div>
    </form>
  );

  const title = useMemo(() => getJoinTitle(domain), [domain]);

  return (
    <div>
      {errorBlock}
      {noticeBlock}

      {domain ? (
        <AuthLayout
          domain={domain}
          title={title}
          shortAboutText={domain.expert_signup_about_mobile}
          longAboutText={domain.expert_signup_about}
        >
          {form}
        </AuthLayout>
      ) : (
        form
      )}
    </div>
  );
};

export default Credentials;
